import { useMemo } from 'react';
import Thumbs from './thumbs';
import Star from './Star';
import { objType } from 'types';
import { categories } from 'utils/colors';
import { words } from 'utils/dictionary';
import { makeStyles } from 'makeStyles';

const Iwidth = 70;

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexFlow: 'column',
  },
  rowContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  image: {
    height: '60%',
    width: '90%',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  fatherAppHeb: {
    width: '100%',
    height: 30,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#FFF',
    // Fix Hebrew text rendering - remove letter spacing and add proper font fallback
    letterSpacing: 0,
    fontFamily: 'Assistant, "Segoe UI", "Arial Hebrew", "Noto Sans Hebrew", "David Libre", sans-serif',
    textRendering: 'optimizeLegibility',
    // Prevent text clipping
    overflow: 'visible',
  },
  category: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 23,
    cursor: 'default',
  },
}));

type TopBarProps = {
  id: string;
  lang: string;
  backgroundColor: string;
  activityUpdated: boolean;
  mouseEnter: boolean;
  showStar: boolean;
  showHeart: boolean;
  showLikes: boolean;
  guest: boolean;
  groupUrl: boolean;
  disabled: boolean;
  homePageApp: boolean;
  showScoresIcon: boolean;
  favoriteStatus: number;
  val: objType;
  hash: objType;
  handleFavoriteClick: (status: number, id: string) => void;
};

const TopBar: React.FC<TopBarProps> = ({
  id,
  lang,
  backgroundColor,
  activityUpdated,
  mouseEnter,
  showStar,
  showHeart,
  showLikes,
  guest,
  groupUrl,
  disabled,
  val,
  hash,
  homePageApp,
  showScoresIcon,
  favoriteStatus,
  handleFavoriteClick,
}) => {
  const { classes } = useStyles();

  // Derive labels/flags safely (no runtime errors if keys missing)
  const [category, showFatherApp, showAuthor] = useMemo(() => {
    const tc = val.origin === 'teacherLessons' || val.origin === 'mivdakim';
    const catKey = categories[val.origin]?.[0];
    const cat =
      (catKey && words[catKey] && words[catKey][lang]) ? words[catKey][lang] : '';

    const fatherHebOK = lang === 'heb' && !!val.father_app_heb && val.father_app_heb.length > 0;
    const fatherArOK  = lang === 'ara' && !!val.father_app_ar  && val.father_app_ar.length  > 0;
    const fath = val.origin === 'lessons' ? (fatherHebOK || fatherArOK) : false;

    const auth = tc && !!val.name_heb && val.name_heb.length > 0;

    return [cat, fath, auth];
  }, [val, lang]);

  const categoryStyle = useMemo(() => {
    return {
      minHeight: (showFatherApp || showAuthor) ? 30 : 50,
      maxHeight: homePageApp ? 50 : undefined,
      width: (guest || homePageApp) ? '100%' : undefined,
    } as React.CSSProperties;
  }, [guest, homePageApp, showFatherApp, showAuthor]);

  // Treat groupUrl as "guest-like" for controls (read-only)
  const effectiveGuestForControls = guest || groupUrl;

  return (
    <div
      className={classes.root}
      style={{ backgroundColor }}
    >
      <div className={classes.rowContainer}>
        {!guest && !homePageApp && (
          <Star
            id={id}
            lang={lang}
            showStar={showStar}
            showScoresIcon={showScoresIcon}
            showHeart={showHeart}
            guest={effectiveGuestForControls}
            Iwidth={Iwidth}
            disabled={disabled}
            mouseEnter={mouseEnter}
            app={val}
            favoriteStatus={favoriteStatus}
            handleFavoriteClick={handleFavoriteClick}
          />
        )}

        <div
          className={classes.category}
          dir="rtl"
          style={categoryStyle}
        >
          {(guest && !val.totalLikes)
            ? `${category} (${words['subscriberContent'][lang]})`
            : category}
        </div>

        {!guest && !homePageApp && (
          <Thumbs
            id={id}
            activityUpdated={activityUpdated}
            showLikes={showLikes}
            hash={hash}
            disabled={disabled}
            Iwidth={Iwidth}
            groupUrl={groupUrl}
          />
        )}
      </div>

      {showFatherApp && (
        <div className={classes.fatherAppHeb} dir="rtl">
          {`${words['fatherApp'][lang]}: ${lang === 'heb' ? (val.father_app_heb || '') : (val.father_app_ar || '')}`}
        </div>
      )}

      {showAuthor && (
        <div className={classes.fatherAppHeb} dir="rtl">
          {val.name_heb}
        </div>
      )}
    </div>
  );
};

export default TopBar;
